import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { PageBody, PageHeader } from "../components/Layout";
import EmptyState from "../components/EmptyState";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
    MagnifyingGlass, ArrowRight, Archive, Trash, BookmarkSimple,
    CaretDown, CaretRight, ChatCircle, Heart, ArrowSquareOut, Sparkle,
} from "@phosphor-icons/react";

const PLATFORMS = ["", "Reddit", "TikTok", "Instagram", "YouTube", "App Store", "Google Play", "Other"];
const RESONANCE = [
    { value: "any", label: "Any resonance" },
    { value: "high", label: "High (≥50 likes)" },
    { value: "medium", label: "Medium (10–49)" },
    { value: "low", label: "Low (<10)" },
];

function ResonanceBadge({ likes }) {
    const n = Number(likes || 0);
    let cls = "bg-secondary text-muted-foreground";
    let label = "—";
    if (n >= 50) { cls = "bg-accent/10 text-accent"; label = "high resonance"; }
    else if (n >= 10) { cls = "bg-primary/10 text-primary"; label = "medium"; }
    else if (n > 0) { cls = "bg-secondary text-muted-foreground"; label = "low"; }
    return (
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-mono ${cls}`}>
            <Heart size={10} weight={n >= 50 ? "fill" : "duotone"} />
            <span className="tabular-nums">{n}</span>
            {n > 0 && <span className="opacity-70 ml-0.5">· {label}</span>}
        </span>
    );
}

function SignalCard({ c, isSelected, onSelect }) {
    const [expanded, setExpanded] = useState(false);
    const text = c.raw_text || "";
    const isLong = text.length > 220;

    return (
        <article
            data-testid={`signal-row-${c.id}`}
            className={`border rounded-sm transition-colors ${isSelected ? "border-primary bg-primary/5" : "border-border bg-card hover:border-foreground/20"}`}
        >
            <div className="flex items-start gap-3 p-3.5">
                <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => onSelect(c.id)}
                    data-testid={`signal-select-${c.id}`}
                    className="mt-1 shrink-0"
                />

                <div className="min-w-0 flex-1">
                    {/* Meta row */}
                    <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono text-muted-foreground mb-1.5">
                        <Badge variant="outline" className="rounded-sm font-mono text-[10px] tracking-wider py-0">{c.platform}</Badge>
                        <ResonanceBadge likes={c.like_count} />
                        {c.reply_count > 0 && (
                            <span className="inline-flex items-center gap-1">
                                <ChatCircle size={10} /> {c.reply_count}
                            </span>
                        )}
                        <span>@{c.author_handle || "anon"}</span>
                        <span>·</span>
                        <span>{(c.imported_at || "").slice(0, 10)}</span>
                        {c.analyzed && <span className="text-primary">· analyzed</span>}
                        {c.saved && <span className="text-accent">· saved</span>}
                    </div>

                    {/* Body — collapsed by default */}
                    <button
                        type="button"
                        onClick={() => isLong && setExpanded(!expanded)}
                        data-testid={`signal-toggle-${c.id}`}
                        className={`text-left w-full text-[14.5px] leading-relaxed text-foreground ${isLong ? "cursor-pointer" : "cursor-default"} ${expanded ? "" : "line-clamp-3"}`}
                    >
                        {text}
                    </button>

                    {/* Tags row */}
                    {(c.topic_tags || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                            {c.topic_tags.slice(0, 4).map((t) => (
                                <span key={t} className="text-[10px] px-1.5 py-0.5 bg-secondary rounded-sm text-secondary-foreground">{t}</span>
                            ))}
                        </div>
                    )}

                    {/* Expanded — metadata + actions */}
                    {expanded && (
                        <div className="mt-3 pt-3 border-t border-border grid grid-cols-2 gap-x-6 gap-y-1 text-[11px] font-mono text-muted-foreground">
                            <div><span className="opacity-70">id</span> {c.id.slice(0, 8)}…</div>
                            {c.posted_at && <div><span className="opacity-70">posted</span> {c.posted_at.slice(0, 16)}</div>}
                            {c.source_id && <div className="truncate"><span className="opacity-70">source</span> {c.source_id.slice(0, 8)}…</div>}
                            {c.stream_id && <div className="truncate"><span className="opacity-70">stream</span> {c.stream_id.slice(0, 8)}…</div>}
                        </div>
                    )}

                    {/* Action row */}
                    <div className="flex items-center gap-1 mt-2 flex-wrap">
                        {isLong && (
                            <button
                                type="button"
                                onClick={() => setExpanded(!expanded)}
                                data-testid={`signal-expand-${c.id}`}
                                className="text-[10px] font-mono uppercase tracking-[0.16em] text-muted-foreground hover:text-primary inline-flex items-center gap-1"
                            >
                                {expanded ? <CaretDown size={10} weight="bold" /> : <CaretRight size={10} weight="bold" />}
                                {expanded ? "Collapse" : "Expand"}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </article>
    );
}

export default function RawSignals() {
    const [comments, setComments] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [filters, setFilters] = useState({
        platform: "",
        search: "",
        analyzed: "",
        saved: "",
        topic: "",
        source_id: "",
        resonance: "any",
    });
    const [sources, setSources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortBy, setSortBy] = useState("recent");  // "recent" | "resonance"
    const navigate = useNavigate();

    const load = async () => {
        const params = {};
        if (filters.platform) params.platform = filters.platform;
        if (filters.search) params.search = filters.search;
        if (filters.analyzed) params.analyzed = filters.analyzed === "yes";
        if (filters.saved) params.saved = filters.saved === "yes";
        if (filters.topic) params.topic = filters.topic;
        if (filters.source_id) params.source_id = filters.source_id;
        if (filters.resonance && filters.resonance !== "any") params.resonance_band = filters.resonance;
        params.sort_by = sortBy;
        params.archived = false;
        params.limit = 400;
        try {
            const r = await api.get("/comments", { params });
            setComments(r.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        api.get("/sources").then((r) => setSources(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        const t = setTimeout(load, 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, sortBy]);

    const filteredAndSorted = useMemo(() => comments, [comments]);

    const toggleSelect = (id) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelected(next);
    };

    const toggleAll = () => {
        if (selected.size === filteredAndSorted.length) setSelected(new Set());
        else setSelected(new Set(filteredAndSorted.map((c) => c.id)));
    };

    const bulk = async (action, tags) => {
        if (selected.size === 0) {
            toast.error("Select signals first");
            return;
        }
        const payload = { comment_ids: Array.from(selected), action };
        if (tags) payload.tags = tags;
        await api.post("/comments/bulk", payload);
        toast.success(`Action '${action}' applied`);
        setSelected(new Set());
        await load();
    };

    const sendToAnalysis = () => {
        if (selected.size === 0) {
            toast.error("Select at least one signal");
            return;
        }
        const ids = Array.from(selected);
        if (ids.length > 60) {
            toast.error(
                `Too many signals selected (${ids.length}). Analyze in batches of 60 or fewer — the LLM context window can't hold more cleanly.`,
                { duration: 6000 }
            );
            return;
        }
        // Stash IDs in sessionStorage instead of URL — long URLs (50+ IDs)
        // exceed common WAF/proxy length limits and silently fail on production.
        try {
            window.sessionStorage.setItem("haevn:pending-analyze-ids", JSON.stringify(ids));
        } catch {
            // sessionStorage disabled — fall back to URL
        }
        navigate(`/analyses?via=session&count=${ids.length}`);
    };

    const allSelected = useMemo(
        () => filteredAndSorted.length > 0 && selected.size === filteredAndSorted.length,
        [filteredAndSorted, selected]
    );

    return (
        <>
            <PageHeader
                overline="Raw Intelligence"
                title="Raw Signals"
                description="The actual language people are using. High-resonance signals — comments with many likes — carry emotional weight beyond their frequency."
                testid="signals-header"
                actions={
                    <Button
                        data-testid="send-to-analysis-btn"
                        size="sm"
                        className="rounded-sm"
                        onClick={sendToAnalysis}
                    >
                        <ArrowRight size={14} weight="bold" className="mr-1.5" />
                        Analyze ({selected.size})
                    </Button>
                }
            />

            <PageBody>
                <div data-testid="signals-filters" className="grid grid-cols-2 md:grid-cols-7 gap-2 mb-5 max-w-6xl">
                    <div className="md:col-span-2 relative">
                        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            data-testid="signals-search"
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            placeholder="Search comments…"
                            className="rounded-sm pl-8 text-sm"
                        />
                    </div>
                    <Select value={filters.platform || "all"} onValueChange={(v) => setFilters({ ...filters, platform: v === "all" ? "" : v })}>
                        <SelectTrigger data-testid="filter-platform" className="rounded-sm text-sm"><SelectValue placeholder="Platform" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All platforms</SelectItem>
                            {PLATFORMS.filter(Boolean).map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                        </SelectContent>
                    </Select>
                    <Select value={filters.resonance} onValueChange={(v) => setFilters({ ...filters, resonance: v })}>
                        <SelectTrigger data-testid="filter-resonance" className="rounded-sm text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {RESONANCE.map((r) => (<SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>))}
                        </SelectContent>
                    </Select>
                    <Select value={filters.analyzed || "any"} onValueChange={(v) => setFilters({ ...filters, analyzed: v === "any" ? "" : v })}>
                        <SelectTrigger data-testid="filter-analyzed" className="rounded-sm text-sm"><SelectValue placeholder="Analyzed?" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="any">Any</SelectItem>
                            <SelectItem value="yes">Analyzed</SelectItem>
                            <SelectItem value="no">Unanalyzed</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={filters.saved || "any"} onValueChange={(v) => setFilters({ ...filters, saved: v === "any" ? "" : v })}>
                        <SelectTrigger data-testid="filter-saved" className="rounded-sm text-sm"><SelectValue placeholder="Saved?" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="any">Any</SelectItem>
                            <SelectItem value="yes">Saved</SelectItem>
                            <SelectItem value="no">Unsaved</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={sortBy} onValueChange={setSortBy}>
                        <SelectTrigger data-testid="sort-by" className="rounded-sm text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="recent">Most recent</SelectItem>
                            <SelectItem value="resonance">Highest resonance</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Compact selection bar */}
                <div className="flex items-center justify-between mb-4 px-1">
                    <div className="flex items-center gap-3">
                        <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-checkbox" />
                        <span className="text-[11px] font-mono uppercase tracking-[0.16em] text-muted-foreground">
                            {filteredAndSorted.length} signals · {selected.size} selected
                        </span>
                    </div>
                    {selected.size > 0 && (
                        <div className="flex items-center gap-1.5">
                            <Button data-testid="bulk-save" size="sm" variant="outline" className="rounded-sm h-7 text-xs" onClick={() => bulk("save")}>
                                <BookmarkSimple size={12} className="mr-1" /> Save
                            </Button>
                            <Button data-testid="bulk-archive" size="sm" variant="outline" className="rounded-sm h-7 text-xs" onClick={() => bulk("archive")}>
                                <Archive size={12} className="mr-1" /> Archive
                            </Button>
                            <Button data-testid="bulk-delete" size="sm" variant="outline" className="rounded-sm h-7 text-xs" onClick={() => bulk("delete")}>
                                <Trash size={12} className="mr-1" /> Delete
                            </Button>
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="text-sm text-muted-foreground">Loading…</div>
                ) : filteredAndSorted.length === 0 ? (
                    <EmptyState
                        title="No signals match these filters"
                        description="Adjust resonance or platform filters, or add a new stream — comments will appear here as they're collected."
                        testid="signals-empty"
                    />
                ) : (
                    <div data-testid="signals-list" className="space-y-2">
                        {filteredAndSorted.map((c) => (
                            <SignalCard
                                key={c.id}
                                c={c}
                                isSelected={selected.has(c.id)}
                                onSelect={toggleSelect}
                            />
                        ))}
                    </div>
                )}
            </PageBody>
        </>
    );
}
